import React, { useState, useEffect, useCallback, useRef } from 'react';
import { WizardStepProps, MessageContent, MessageTemplate, DYNAMIC_VARIABLES } from '../../../types';
import { getMessageTemplates } from '../../../utils/campaignApi';
import CampaignAnalyzerModal from '../CampaignAnalyzerModal';
import { supabase } from '../../../supabase/client';

// ── ICP pre-population (Epoch 78) ─────────────────────────────────────────────

interface ICPContext {
  target_role?: string;
  company_type?: string;
  geography?: string;
  industry?: string;
  company_size?: string;
}

function extractICPFields(row: { request_name: string; filters: any }): ICPContext {
  const f = row.filters || {};
  // Actual DB schema uses "position", "location", "industry" — not "role"/"geography"/"company"
  const position = f.position || {};
  const location = f.location || {};
  const industryObj = f.industry || {};
  const company = f.company || {};

  // titles is a comma-separated string; fall back to functions/seniority arrays
  const target_role = position.titles?.trim() ||
    [...(position.functions || []), ...(position.seniority || [])].filter(Boolean).join(', ') ||
    undefined;

  // Combine all location sub-fields, deduplicate, cap at 3
  const geoRaw = [
    ...(location.locations || []),
    ...(location.countries || []),
    ...(location.states || []),
  ].filter(Boolean);
  const geography = [...new Set(geoRaw)].slice(0, 3).join(', ') || undefined;

  const industry = (industryObj.industries || []).join(', ') || undefined;
  const company_type = (company.types || []).join(', ') || undefined;
  const company_size = (company.sizes || []).join(', ') || undefined;

  return { target_role, company_type, geography, industry, company_size };
}

const MessageSequenceBuilderStep: React.FC<WizardStepProps> = ({
  state,
  updateState,
  errors,
  isFirstCampaign
}) => {
  const [availableTemplates, setAvailableTemplates] = useState<MessageTemplate[]>([]);
  const [, setSelectedTemplateId] = useState<string>('');
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [showVariableHelper, setShowVariableHelper] = useState(false);
  const [activeMessageIndex, setActiveMessageIndex] = useState(0);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [delayUnits, setDelayUnits] = useState<{ [key: number]: 'hours' | 'days' }>({});
  // Epoch 71: Onboarding panel state
  const [showOnboardingPanel, setShowOnboardingPanel] = useState(true);
  // Epoch 77: AI analyzer modal
  const [analyzerOpen, setAnalyzerOpen] = useState(false);
  // Epoch 78: ICP context + button loading
  const [analyzeButtonLoading, setAnalyzeButtonLoading] = useState(false);
  const analyzerContextRef = useRef<ICPContext>({});

  // Helper function to get message label
  const getMessageLabel = (messageNumber: number) => {
    if (messageNumber === 1) {
      return "LinkedIn Connection Request Message";
    } else if (messageNumber === 2) {
      return "After They Connect (Message 2)";
    } else {
      return `Message ${messageNumber}`;
    }
  };

  // Initialize with at least one message
  useEffect(() => {
    if (state.messages.length === 0) {
      updateState({
        messages: [{
          message_number: 1,
          content: '',
          character_count: 0,
          delay_days: 0
        }]
      });
    }
  }, [state.messages.length, updateState]);

  const loadAvailableTemplates = useCallback(async () => {
    setIsLoadingTemplates(true);
    try {
      const result = await getMessageTemplates({
        serviceTier: state.service_tier,
        includePublic: true,
        limit: 50
      });
      
      if (result.success && result.data) {
        setAvailableTemplates(result.data);
      } else {
        console.error('Failed to load templates:', result.error);
        // Keep empty array as fallback
        setAvailableTemplates([]);
      }
    } catch (error) {
      console.error('Network error loading templates:', error);
      setAvailableTemplates([]);
    } finally {
      setIsLoadingTemplates(false);
    }
  }, [state.service_tier]);

  // Load available templates
  useEffect(() => {
    loadAvailableTemplates();
  }, [loadAvailableTemplates]);

  const updateMessage = useCallback((index: number, updates: Partial<MessageContent>) => {
    const updatedMessages = [...state.messages];
    updatedMessages[index] = { ...updatedMessages[index], ...updates };
    
    // Auto-calculate character count if content changed
    if (updates.content !== undefined) {
      updatedMessages[index].character_count = updates.content.length;
    }
    
    updateState({ messages: updatedMessages });
  }, [state.messages, updateState]);

  const addMessage = () => {
    if (state.messages.length < 8) {
      const newMessage: MessageContent = {
        message_number: state.messages.length + 1,
        content: '',
        character_count: 0,
        delay_days: state.messages.length > 0 ? 3 : 0
      };
      
      updateState({ messages: [...state.messages, newMessage] });
      setActiveMessageIndex(state.messages.length);
    }
  };

  const removeMessage = (index: number) => {
    if (state.messages.length > 1) {
      const updatedMessages = state.messages
        .filter((_, i) => i !== index)
        .map((msg, i) => ({ ...msg, message_number: i + 1 }));
      
      updateState({ messages: updatedMessages });
      
      // Adjust active index if needed
      if (activeMessageIndex >= updatedMessages.length) {
        setActiveMessageIndex(updatedMessages.length - 1);
      }
    }
  };

  const insertVariable = (variable: string) => {
    const message = state.messages[activeMessageIndex];
    if (message) {
      const textarea = document.getElementById(`message-${activeMessageIndex}`) as HTMLTextAreaElement;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const currentContent = message.content;
        const newContent = currentContent.slice(0, start) + variable + currentContent.slice(end);
        
        updateMessage(activeMessageIndex, { content: newContent });
        
        // Restore cursor position
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + variable.length, start + variable.length);
        }, 0);
      }
    }
  };

  const applyTemplate = (templateId: string) => {
    const template = availableTemplates.find(t => t.id === templateId);
    if (template) {
      updateState({
        sequence_name: state.sequence_name || template.template_name,
        messages: template.messages.map((msg, index) => ({
          ...msg,
          message_number: index + 1
        })),
        use_existing_template: true,
        selected_template_id: templateId
      });
      setSelectedTemplateId(templateId);
      setShowTemplateModal(false);
    }
  };

  const canProceed = () => {
    const hasSequenceName = state.sequence_name?.trim();
    const sequenceNameLongEnough = (state.sequence_name?.trim()?.length || 0) >= 3;
    const hasMessages = state.messages.length > 0;
    const allMessagesHaveContent = state.messages.every(msg => msg.content.trim().length > 0);
    
    // Check for validation errors from the wizard
    const hasValidationErrors = errors && Object.keys(errors).length > 0;
    
    // Debug logging removed to prevent console spam
    
    return hasSequenceName && sequenceNameLongEnough && hasMessages && allMessagesHaveContent && !hasValidationErrors;
  };

  const getTotalCharacters = () => {
    return state.messages.reduce((total, msg) => total + msg.character_count, 0);
  };

  const getVariableUsage = () => {
    const usage: Record<string, number> = {};
    state.messages.forEach(msg => {
      DYNAMIC_VARIABLES.forEach(variable => {
        const count = (msg.content.match(new RegExp(variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g')) || []).length;
        if (count > 0) {
          usage[variable] = (usage[variable] || 0) + count;
        }
      });
    });
    return usage;
  };

  const handleAnalyzeClick = async () => {
    setAnalyzeButtonLoading(true);
    let icpContext: ICPContext = {};

    if (state.campaign_id) {
      try {
        // Primary: filter request linked directly to this campaign
        const { data } = await supabase
          .from('preset_filter_requests')
          .select('request_name, filters')
          .eq('campaign_id', state.campaign_id)
          .order('created_at', { ascending: false })
          .limit(1);

        if (data && data.length > 0 && data[0]?.filters) {
          icpContext = extractICPFields(data[0]);
        } else {
          // Fallback: past_list campaigns reference a filter request via lead_source_id
          const { data: campaignRow } = await supabase
            .from('campaigns')
            .select('lead_source_id, lead_source_type')
            .eq('id', state.campaign_id)
            .maybeSingle();

          if (campaignRow?.lead_source_type === 'past_list' && campaignRow?.lead_source_id) {
            const { data: pfr } = await supabase
              .from('preset_filter_requests')
              .select('request_name, filters')
              .eq('id', campaignRow.lead_source_id)
              .maybeSingle();

            if (pfr?.filters) {
              icpContext = extractICPFields(pfr);
            }
          }
        }
      } catch (_e) {
        // fail open — modal opens without ICP context
      }
    }

    // Write ref synchronously so modal reads the latest value on first render
    analyzerContextRef.current = icpContext;
    setAnalyzeButtonLoading(false);
    setAnalyzerOpen(true);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">LinkedIn Connection Request Message Sequence</h2>
        <p className="text-gray-600">
          Write a LinkedIn Connection Request drip sequence for up to 8 messages
        </p>
      </div>

      {/* Epoch 71: Onboarding Panel - Template Cards (first-time users with empty messages) */}
      {isFirstCampaign && showOnboardingPanel && state.messages.every(m => !m.content) && availableTemplates.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-2">
              <span className="text-xl">🚀</span>
              <h3 className="text-lg font-semibold text-gray-900">Get Started Quickly — Use a Proven Template</h3>
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Pick a template below to pre-fill your message sequence. You can customize every message after applying it.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            {availableTemplates
              .sort((a, b) => b.messages.length - a.messages.length)
              .slice(0, 3)
              .map((template) => (
                <div
                  key={template.id}
                  className="bg-white rounded-lg border border-gray-200 p-4 hover:border-blue-400 hover:shadow-md cursor-pointer transition-all"
                  onClick={() => {
                    applyTemplate(template.id);
                    setShowOnboardingPanel(false);
                  }}
                >
                  <h4 className="font-medium text-gray-900 text-sm mb-2 truncate">{template.template_name}</h4>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mb-2">
                    {template.messages.length} messages
                  </span>
                  <p className="text-xs text-gray-500 line-clamp-2">
                    {template.messages[0]?.content
                      ? `"${template.messages[0].content.substring(0, 80)}${template.messages[0].content.length > 80 ? '...' : ''}"`
                      : 'No preview available'}
                  </p>
                  <button className="mt-3 w-full text-center text-xs font-medium text-blue-600 hover:text-blue-800 py-1.5 border border-blue-200 rounded-md hover:bg-blue-50 transition-colors">
                    Use Template
                  </button>
                </div>
              ))}
          </div>

          <button
            onClick={() => setShowOnboardingPanel(false)}
            className="text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Or write your own from scratch
          </button>
        </div>
      )}

      {/* Epoch 71: Tips Box (first-time users — always visible) */}
      {isFirstCampaign && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
          <div className="flex items-center space-x-2 mb-3">
            <span className="text-lg">💡</span>
            <h3 className="text-sm font-semibold text-blue-900">3 Tips for Effective LinkedIn Campaigns</h3>
          </div>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start space-x-2">
              <span className="text-blue-400 mt-0.5">•</span>
              <span>Keep your connection request short (under 200 chars). Lead with a pain point or observation, not a pitch.</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-blue-400 mt-0.5">•</span>
              <span>Use 3-5 follow-up messages. Most replies come after message 2 or 3 — don't rely on a single message.</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-blue-400 mt-0.5">•</span>
              <span>Personalize with variables like <code className="bg-blue-100 px-1 rounded text-xs">%%first_name%%</code> and <code className="bg-blue-100 px-1 rounded text-xs">%%company%%</code> — they get inserted automatically per lead.</span>
            </li>
          </ul>
        </div>
      )}

      {/* Sequence Name */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <label htmlFor="sequence-name" className="text-lg font-semibold text-gray-900">
            Drip Sequence Name
          </label>
          <button
            onClick={() => {
              loadAvailableTemplates();
              setShowTemplateModal(true);
            }}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Use Template
          </button>
        </div>
        <input
          id="sequence-name"
          type="text"
          value={state.sequence_name || ''}
          onChange={(e) => updateState({ sequence_name: e.target.value })}
          placeholder="Enter a name for your drip sequence (minimum 3 characters)..."
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors?.sequence_name ? 'border-red-300 bg-red-50' : 'border-gray-300'
          }`}
        />
        {errors?.sequence_name && (
          <p className="mt-2 text-sm text-red-600">{errors.sequence_name[0]}</p>
        )}
        {!errors?.sequence_name && state.sequence_name && state.sequence_name.length < 3 && (
          <p className="mt-2 text-sm text-amber-600">
            Drip sequence name must be at least 3 characters ({state.sequence_name.length}/3)
          </p>
        )}
      </div>

      {/* Messages */}
      <div className="bg-white rounded-lg border border-gray-200">
        {/* Message Tabs */}
        <div className="border-b border-gray-200 px-6 pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Messages</h3>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowVariableHelper(!showVariableHelper)}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                {showVariableHelper ? 'Hide' : 'Show'} Variables
              </button>
              <button
                onClick={addMessage}
                disabled={state.messages.length >= 8}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Message
              </button>
              {state.messages.some(m => m.content?.trim()) && (
                <button
                  onClick={handleAnalyzeClick}
                  disabled={analyzeButtonLoading}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors ${analyzeButtonLoading ? 'opacity-70 cursor-wait' : ''}`}
                >
                  {analyzeButtonLoading ? (
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <span>✨</span>
                  )}
                  Analyze with AI
                </button>
              )}
            </div>
          </div>
          
          <div className="flex space-x-1 overflow-x-auto">
            {state.messages.map((message, index) => (
              <button
                key={index}
                onClick={() => setActiveMessageIndex(index)}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors whitespace-nowrap ${
                  activeMessageIndex === index
                    ? 'text-blue-600 border-blue-600 bg-blue-50'
                    : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {getMessageLabel(message.message_number)}
                {message.content.trim() && (
                  <span className="ml-1 inline-flex items-center justify-center w-2 h-2 bg-green-500 rounded-full"></span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Variable Helper */}
        {showVariableHelper && (
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Available Variables</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {DYNAMIC_VARIABLES.map((variable) => (
                <button
                  key={variable}
                  onClick={() => insertVariable(variable)}
                  className="text-left px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-colors"
                >
                  <code className="text-blue-600 font-mono">{variable}</code>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Active Message Editor */}
        <div className="p-6">
          {state.messages[activeMessageIndex] && (
            <div className="space-y-4">
              {/* Message Settings */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-gray-700">
                    {getMessageLabel(activeMessageIndex + 1)}
                  </span>
                  {activeMessageIndex > 0 && (
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-600">Send after:</label>
                      <input
                        type="number"
                        min="0"
                        max="999"
                        value={(() => {
                          const delayValue = state.messages[activeMessageIndex].delay_days || 0;
                          const unit = delayUnits[activeMessageIndex] || (delayValue < 1 && delayValue > 0 ? 'hours' : 'days');
                          
                          if (unit === 'hours') {
                            // If stored as fraction of day, convert to hours for display
                            return Math.round(delayValue * 24);
                          }
                          return delayValue;
                        })()}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 0;
                          const unit = delayUnits[activeMessageIndex] || 'days';
                          
                          if (unit === 'hours') {
                            // Store hours as fraction of days
                            updateMessage(activeMessageIndex, { delay_days: value / 24 });
                          } else {
                            // Store days directly
                            updateMessage(activeMessageIndex, { delay_days: value });
                          }
                        }}
                        className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                      />
                      <select
                        value={delayUnits[activeMessageIndex] || (state.messages[activeMessageIndex].delay_days < 1 && state.messages[activeMessageIndex].delay_days > 0 ? 'hours' : 'days')}
                        onChange={(e) => {
                          const newUnit = e.target.value as 'hours' | 'days';
                          const currentDelayDays = state.messages[activeMessageIndex].delay_days || 0;
                          
                          setDelayUnits(prev => ({ ...prev, [activeMessageIndex]: newUnit }));
                          
                          // Convert the current value when switching units
                          if (newUnit === 'hours' && currentDelayDays >= 1) {
                            // Converting from days to hours - keep the display value same
                            // e.g., 3 days becomes 3 hours (stored as 3/24 = 0.125 days)
                            updateMessage(activeMessageIndex, { delay_days: currentDelayDays / 24 });
                          } else if (newUnit === 'days' && currentDelayDays < 1) {
                            // Converting from hours to days - keep the display value same
                            // e.g., 3 hours (stored as 0.125) becomes 3 days
                            updateMessage(activeMessageIndex, { delay_days: Math.round(currentDelayDays * 24) });
                          }
                        }}
                        className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="hours">hours</option>
                        <option value="days">days</option>
                      </select>
                    </div>
                  )}
                </div>
                
                {state.messages.length > 1 && (
                  <button
                    onClick={() => removeMessage(activeMessageIndex)}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Remove Message
                  </button>
                )}
              </div>

              {/* Message Content */}
              <div>
                <textarea
                  id={`message-${activeMessageIndex}`}
                  value={state.messages[activeMessageIndex].content}
                  onChange={(e) => updateMessage(activeMessageIndex, { content: e.target.value })}
                  placeholder="Write your LinkedIn message here... Use variables like %%first_name%% for personalization."
                  rows={6}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                    state.messages[activeMessageIndex].content.trim().length === 0 
                      ? 'border-red-300 bg-red-50' 
                      : 'border-gray-300'
                  }`}
                />
                
                {state.messages[activeMessageIndex].content.trim().length === 0 && (
                  <p className="mt-1 text-sm text-red-600">Message content is required</p>
                )}
                
                {/* Character Count */}
                <div className="flex items-center justify-between mt-2 text-sm">
                  {activeMessageIndex === 0 ? (
                    // First message - show character limit and warnings
                    <>
                      <span className={`${
                        state.messages[activeMessageIndex].character_count > 300 
                          ? 'text-red-600' 
                          : state.messages[activeMessageIndex].character_count > 250 
                          ? 'text-amber-600' 
                          : 'text-gray-500'
                      }`}>
                        {state.messages[activeMessageIndex].character_count}/300 characters
                      </span>
                      
                      {state.messages[activeMessageIndex].character_count > 300 && (
                        <span className="text-red-600 text-xs">LinkedIn messages are best kept under 300 characters</span>
                      )}
                    </>
                  ) : (
                    // Follow-up messages - no character limit
                    <span className="text-gray-500">
                      {state.messages[activeMessageIndex].character_count} characters (no limit for follow-up messages)
                    </span>
                  )}
                </div>
              </div>

              {/* Message Preview */}
              {state.messages[activeMessageIndex].content && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h5 className="text-sm font-medium text-gray-900 mb-2">Preview</h5>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap">
                    {state.messages[activeMessageIndex].content
                      .replace(/%%first_name%%/g, 'John')
                      .replace(/%%last_name%%/g, 'Smith')
                      .replace(/%%company%%/g, 'TechCorp')
                      .replace(/%%position%%/g, 'VP of Sales')
                      .replace(/%%city%%/g, 'San Francisco')
                      .replace(/%%industry%%/g, 'Technology')
                      .replace(/%%topskill%%/g, 'Sales Leadership')
                      .replace(/%%mutualconnectionname%%/g, 'Sarah Johnson')
                      .replace(/%%timeincurrentrole%%/g, '2 years')
                      .replace(/%%linkedinurl%%/g, 'linkedin.com/in/johnsmith')
                      .replace(/%%customvariable%%/g, 'Custom Value')
                    }
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Epoch 71: Message Count Nudge (ALL users - single message with content) */}
      {state.messages.length === 1 && state.messages[0]?.content?.trim().length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <span className="text-amber-500 text-lg mt-0.5">⚠️</span>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-amber-800 mb-1">Single-Message Campaign</h4>
              <p className="text-sm text-amber-700 mb-3">
                Campaigns with 3-5 messages see significantly higher response rates. Consider adding follow-up messages.
              </p>
              <button
                onClick={addMessage}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-amber-800 bg-amber-100 border border-amber-300 rounded-md hover:bg-amber-200 transition-colors"
              >
                + Add Follow-up Message
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Sequence Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-600 mb-1">Total Messages</div>
          <div className="text-2xl font-bold text-blue-600">{state.messages.length}</div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-600 mb-1">Total Characters</div>
          <div className="text-2xl font-bold text-blue-600">
            {state.messages.length > 1 ? "No limit" : getTotalCharacters()}
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-600 mb-1">Variables Used</div>
          <div className="text-2xl font-bold text-blue-600">
            {Object.keys(getVariableUsage()).length}
          </div>
        </div>
      </div>

      {/* Template Save Options */}
      {canProceed() && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center space-x-4">
            <input
              type="checkbox"
              id="save-as-template"
              checked={state.save_as_template || false}
              onChange={(e) => {
                const checked = e.target.checked;
                if (checked) {
                  // Initialize template_metadata with sequence name when toggling on
                  updateState({
                    save_as_template: true,
                    template_metadata: {
                      template_name: state.sequence_name || state.campaign_name || '',
                      template_description: state.template_metadata?.template_description || '',
                      category: state.template_metadata?.category || 'custom',
                      tags: state.template_metadata?.tags || []
                    }
                  });
                } else {
                  updateState({ save_as_template: false });
                }
              }}
              className="h-4 w-4 text-blue-600 rounded border-gray-300"
            />
            <label htmlFor="save-as-template" className="text-sm font-medium text-gray-700">
              Save this sequence as a reusable template
            </label>
          </div>

          {state.save_as_template && (
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Name
                </label>
                <input
                  type="text"
                  value={state.template_metadata?.template_name || state.sequence_name || ''}
                  onChange={(e) => updateState({
                    template_metadata: {
                      ...state.template_metadata,
                      template_name: e.target.value,
                      template_description: state.template_metadata?.template_description || '',
                      category: state.template_metadata?.category || 'custom',
                      tags: state.template_metadata?.tags || []
                    }
                  })}
                  placeholder="Enter template name..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={state.template_metadata?.template_description || ''}
                  onChange={(e) => updateState({
                    template_metadata: {
                      ...state.template_metadata,
                      template_name: state.template_metadata?.template_name || '',
                      template_description: e.target.value,
                      category: state.template_metadata?.category || 'custom',
                      tags: state.template_metadata?.tags || []
                    }
                  })}
                  placeholder="Describe when to use this template..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error Display */}
      {errors && Object.keys(errors).length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-sm text-red-700">
            <p className="font-medium mb-2">Validation Errors:</p>
            {Object.entries(errors).map(([field, fieldErrors]) => (
              <div key={field} className="mb-2">
                <strong>{field}:</strong>
                {fieldErrors.map((error, index) => (
                  <p key={index} className="ml-2">• {error}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Progress Summary */}
      {!canProceed() && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="text-sm text-yellow-800">
            <p className="font-medium mb-2">Complete these items to continue:</p>
            <ul className="space-y-1">
              {!state.sequence_name?.trim() && (
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                  Enter a drip sequence name
                </li>
              )}
              {state.sequence_name?.trim() && (state.sequence_name?.trim()?.length || 0) < 3 && (
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                  Drip sequence name must be at least 3 characters ({state.sequence_name?.trim()?.length || 0}/3)
                </li>
              )}
              {state.messages.length === 0 && (
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                  Add at least one message
                </li>
              )}
              {state.messages.some(msg => msg.content.trim().length === 0) && (
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                  Fill in content for all messages ({state.messages.filter(msg => msg.content.trim().length > 0).length}/{state.messages.length} completed)
                </li>
              )}
              {errors && Object.keys(errors).length > 0 && (
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                  Fix validation errors shown above
                </li>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* Debug Info */}
      {process.env.NODE_ENV === 'development' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-2">Debug Info:</p>
            <p><strong>Sequence Name:</strong> "{state.sequence_name}" (length: {state.sequence_name?.length || 0})</p>
            <p><strong>Messages Count:</strong> {state.messages.length}</p>
            <p><strong>Can Proceed:</strong> {canProceed() ? 'YES' : 'NO'}</p>
            <p><strong>Messages Content:</strong></p>
            {state.messages.map((msg, idx) => (
              <p key={idx} className="ml-4">
                Message {idx + 1}: "{msg.content}" (length: {msg.content.length}, trimmed: {msg.content.trim().length})
              </p>
            ))}
          </div>
        </div>
      )}


      {/* Template Selection Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Choose a Template</h3>
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              {isLoadingTemplates ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : availableTemplates.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-lg font-medium mb-2">No templates available</p>
                  <p className="text-sm">Save a message sequence as a template to reuse it in future campaigns.</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {availableTemplates.map((template) => (
                    <div
                      key={template.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 cursor-pointer transition-colors"
                      onClick={() => applyTemplate(template.id)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-gray-900">{template.template_name}</h4>
                          <p className="text-sm text-gray-600 mt-1">{template.template_description}</p>
                        </div>
                        <div className="flex items-center space-x-2 text-xs text-gray-500">
                          <span>{template.messages.length} messages</span>
                          <span>•</span>
                          <span>Used {template.usage_count} times</span>
                        </div>
                      </div>

                      {template.tags && template.tags.length > 0 && (
                        <div className="flex items-center space-x-2">
                          {template.tags.map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Epoch 77/78/79: AI Campaign Analyzer modal */}
      <CampaignAnalyzerModal
        isOpen={analyzerOpen}
        onClose={() => setAnalyzerOpen(false)}
        messages={state.messages}
        campaignContext={{
          campaign_name: state.campaign_name,
          sequence_name: state.sequence_name,
          ...analyzerContextRef.current,
        }}
        campaignId={state.campaign_id}
        initialGoal={state.campaignGoal ?? null}
        onLoadMessages={(newMessages) => {
          updateState({ messages: newMessages });
          setAnalyzerOpen(false);
        }}
      />
    </div>
  );
};

export default MessageSequenceBuilderStep;